import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, Car, Receipt, Fuel, 
  Wrench, Shield, FileText, Calendar, Filter, Download, RefreshCw,
  PlusCircle, ChevronDown, Eye, Banknote, CreditCard, X, 
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart, Users,
  Settings, Percent, Building2, Truck, CheckCircle2
} from 'lucide-react';
import api from '../../services/api';
import { format, parseISO, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { sq } from 'date-fns/locale/sq';
import { toast } from 'sonner';

const FinancePage = () => {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [revenueByEntity, setRevenueByEntity] = useState(null);
  const [vatSettings, setVatSettings] = useState({
    vat_enabled: false,
    vat_percentage: 20.0,
    vat_registration_number: null,
    vat_included_in_price: true
  });
  
  // Filters
  const [dateFilter, setDateFilter] = useState('month');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [entityType, setEntityType] = useState('driver');

  // Modals
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showVatSettingsModal, setShowVatSettingsModal] = useState(false);
  const [newExpense, setNewExpense] = useState({
    vehicle_id: '',
    category: 'fuel',
    description: '',
    amount_lek: '',
    payment_method: 'cash',
    expense_date: new Date().toISOString().split('T')[0],
    odometer_km: '',
    liters: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch VAT settings
      const vatRes = await api.get('/finance/vat-settings');
      setVatSettings(vatRes.data);
      
      // Fetch vehicles
      const vehiclesRes = await api.get('/fleet/vehicles');
      setVehicles(vehiclesRes.data || []);
      
      // Fetch finance summary
      const summaryParams = { period: dateFilter };
      if (dateFilter === 'custom' && customDateFrom && customDateTo) {
        summaryParams.date_from = new Date(customDateFrom).toISOString();
        summaryParams.date_to = new Date(customDateTo).toISOString();
      }
      if (selectedVehicle !== 'all') {
        summaryParams.vehicle_id = selectedVehicle;
      }
      const summaryRes = await api.get('/finance/summary', { params: summaryParams });
      setSummary(summaryRes.data);
      
      // Fetch daily breakdown for charts
      const dailyRes = await api.get('/finance/daily-breakdown', { params: { period: dateFilter } });
      setDailyData(dailyRes.data);
      
      // Fetch top performers
      const topRes = await api.get('/finance/top-performers', { params: { period: dateFilter, limit: 5 } });
      setTopPerformers(topRes.data);
      
      // Fetch revenue by entity
      const entityRes = await api.get('/finance/revenue-by-entity', { 
        params: { entity_type: entityType, period: dateFilter } 
      });
      setRevenueByEntity(entityRes.data);
      
      // Fetch expenses
      const expensesRes = await api.get('/finance/expenses', { 
        params: { period: dateFilter, limit: 100 }
      });
      setExpenses(expensesRes.data.expenses || []);
      
    } catch (error) {
      console.error('Error fetching finance data:', error);
      toast.error(error.response?.data?.detail || 'Gabim në ngarkimin e financave');
    } finally {
      setLoading(false);
    }
  }, [dateFilter, customDateFrom, customDateTo, selectedVehicle, entityType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatPrice = (amount) => {
    return Number(amount || 0).toLocaleString('sq-AL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      fuel: 'Karburant',
      service: 'Servis',
      insurance: 'Sigurim',
      tax: 'Taksë',
      inspection: 'Kolaudim',
      repair: 'Riparim',
      salary: 'Rroga',
      other: 'Tjetër'
    };
    return labels[cat] || cat;
  };

  const getCategoryIcon = (cat) => {
    const icons = {
      fuel: Fuel,
      service: Wrench,
      insurance: Shield,
      tax: FileText,
      inspection: FileText,
      repair: Wrench,
      salary: Users,
      other: Receipt
    };
    return icons[cat] || Receipt;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      fuel: 'bg-orange-100 text-orange-700',
      service: 'bg-blue-100 text-blue-700',
      insurance: 'bg-green-100 text-green-700',
      tax: 'bg-purple-100 text-purple-700',
      inspection: 'bg-yellow-100 text-yellow-700',
      repair: 'bg-red-100 text-red-700',
      salary: 'bg-indigo-100 text-indigo-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return colors[cat] || 'bg-gray-100 text-gray-700';
  };

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'today': return 'Sot';
      case 'week': return 'Kjo Javë';
      case 'month': return 'Ky Muaj';
      case 'year': return 'Ky Vit';
      case 'custom': return 'Periudhë';
      default: return 'Ky Muaj';
    }
  };

  const handleExportReport = async (reportType, format) => {
    try {
      const params = new URLSearchParams({
        report_type: reportType,
        report_format: format,
        period: dateFilter
      });
      
      if (dateFilter === 'custom' && customDateFrom && customDateTo) {
        params.append('date_from', new Date(customDateFrom).toISOString());
        params.append('date_to', new Date(customDateTo).toISOString());
      }
      
      const response = await api.post(`/reports/generate?${params.toString()}`, {}, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_${reportType}_${dateFilter}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Gabim në shkarkimin e raportit');
    }
  };

  const handleAddExpense = async () => {
    try {
      const params = new URLSearchParams({
        vehicle_id: newExpense.vehicle_id,
        category: newExpense.category,
        description: newExpense.description,
        amount_lek: newExpense.amount_lek,
        payment_method: newExpense.payment_method
      });
      
      if (newExpense.expense_date) {
        params.append('expense_date', new Date(newExpense.expense_date).toISOString());
      }
      if (newExpense.odometer_km) {
        params.append('odometer_km', newExpense.odometer_km);
      }
      if (newExpense.liters) {
        params.append('liters', newExpense.liters);
      }
      if (newExpense.notes) {
        params.append('notes', newExpense.notes);
      }
      
      await api.post(`/finance/expenses?${params.toString()}`);
      setShowAddExpenseModal(false);
      setNewExpense({
        vehicle_id: '',
        category: 'fuel',
        description: '',
        amount_lek: '',
        payment_method: 'cash',
        expense_date: new Date().toISOString().split('T')[0],
        odometer_km: '',
        liters: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error adding expense:', error);
      alert('Gabim në shtimin e shpenzimit');
    }
  };

  const handleUpdateVatSettings = async () => {
    try {
      const params = new URLSearchParams({
        vat_enabled: vatSettings.vat_enabled,
        vat_percentage: vatSettings.vat_percentage,
        vat_included_in_price: vatSettings.vat_included_in_price
      });
      if (vatSettings.vat_registration_number) {
        params.append('vat_registration_number', vatSettings.vat_registration_number);
      }
      
      await api.put(`/finance/vat-settings?${params.toString()}`);
      setShowVatSettingsModal(false);
      fetchData();
    } catch (error) {
      console.error('Error updating VAT settings:', error);
      alert('Gabim në përditësimin e TVSH');
    }
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, labels, color = 'bg-green-500', maxBars = 30 }) => {
    if (!data || !labels) return null;
    const displayData = data.slice(-maxBars);
    const displayLabels = labels.slice(-maxBars);
    const maxValue = Math.max(...displayData, 1);
    
    return (
      <div className="flex items-end gap-1 h-32">
        {displayData.map((value, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center group relative">
            <div 
              className={`w-full ${color} rounded-t transition-all hover:opacity-80`}
              style={{ height: `${(value / maxValue) * 100}%`, minHeight: value > 0 ? '4px' : '0' }}
            />
            <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
              {displayLabels[idx]?.split('-').slice(1).join('/')}: {formatPrice(value)} L
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financat & Kontabiliteti</h1>
          <p className="text-sm text-gray-500">Menaxho të ardhurat, shpenzimet dhe raportet financiare</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowVatSettingsModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
            data-testid="vat-settings-btn"
          >
            <Settings className="w-4 h-4" />
            TVSH
            {vatSettings.vat_enabled && (
              <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                {vatSettings.vat_percentage}%
              </span>
            )}
          </button>
          <button 
            onClick={() => setShowAddExpenseModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
            data-testid="add-expense-btn"
          >
            <PlusCircle className="w-4 h-4" />
            Shto Shpenzim
          </button>
          <button 
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Të Ardhura ({getDateFilterLabel()})</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(summary?.total_revenue_lek)} L</p>
              <div className="flex items-center gap-1 mt-1 text-green-200 text-xs">
                {summary?.revenue_vs_previous_percent >= 0 ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                <span>{summary?.revenue_vs_previous_percent || 0}% vs para</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Shpenzime</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(summary?.total_expenses_lek)} L</p>
              <div className="flex items-center gap-1 mt-1 text-red-200 text-xs">
                <Receipt className="w-3 h-3" />
                <span>{expenses.length} transaksione</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingDown className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-br ${(summary?.gross_profit_lek || 0) >= 0 ? 'from-emerald-500 to-emerald-600' : 'from-orange-500 to-orange-600'} rounded-xl p-5 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Fitimi Bruto</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(summary?.gross_profit_lek)} L</p>
              <div className="flex items-center gap-1 mt-1 text-white/70 text-xs">
                <Percent className="w-3 h-3" />
                <span>{summary?.profit_margin_percent || 0}% marzh</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
        </div>

        {vatSettings.vat_enabled && (
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">TVSH ({vatSettings.vat_percentage}%)</p>
                <p className="text-3xl font-bold mt-1">{formatPrice(summary?.total_vat_collected_lek)} L</p>
                <div className="flex items-center gap-1 mt-1 text-purple-200 text-xs">
                  <Building2 className="w-3 h-3" />
                  <span>për shtetin</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Udhëtime</p>
              <p className="text-3xl font-bold mt-1">{summary?.completed_rides || 0}</p>
              <div className="flex items-center gap-1 mt-1 text-blue-200 text-xs">
                <Car className="w-3 h-3" />
                <span>{formatPrice(summary?.total_distance_km)} km</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Car className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        {/* Date Filter */}
        <div className="relative">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            data-testid="date-filter-btn"
          >
            <Calendar className="w-4 h-4 text-gray-500" />
            <span>{getDateFilterLabel()}</span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          
          {showDatePicker && (
            <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-72 p-4">
              <div className="space-y-2">
                {[
                  { key: 'today', label: 'Sot' },
                  { key: 'week', label: 'Kjo Javë' },
                  { key: 'month', label: 'Ky Muaj' },
                  { key: 'year', label: 'Ky Vit' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => { setDateFilter(opt.key); setShowDatePicker(false); }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition ${
                      dateFilter === opt.key ? 'bg-red-50 text-red-600 font-medium' : 'hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
                
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Periudhë e Personalizuar</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-gray-400">-</span>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <button
                    onClick={() => { setDateFilter('custom'); setShowDatePicker(false); }}
                    disabled={!customDateFrom || !customDateTo}
                    className="w-full mt-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apliko
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Vehicle Filter */}
        <select
          value={selectedVehicle}
          onChange={(e) => setSelectedVehicle(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          data-testid="vehicle-filter"
        >
          <option value="all">Të gjitha Automjetet</option>
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>{v.plate} - {v.model}</option>
          ))}
        </select>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 ml-auto">
          {[
            { key: 'overview', label: 'Përmbledhje', icon: BarChart3 },
            { key: 'by-entity', label: 'Sipas Entitetit', icon: Users },
            { key: 'expenses', label: 'Shpenzime', icon: TrendingDown },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab.key ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Export Dropdown */}
        <div className="relative group">
          <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            <Download className="w-4 h-4" />
            Eksporto
            <ChevronDown className="w-4 h-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all w-48">
            <div className="py-1">
              <p className="px-3 py-1 text-xs text-gray-500 font-medium uppercase">Raport të Ardhurave</p>
              <button
                onClick={() => handleExportReport('revenue', 'pdf')}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <FileText className="w-4 h-4 text-red-500" />
                PDF
              </button>
              <button
                onClick={() => handleExportReport('revenue', 'excel')}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4 text-green-500" />
                Excel
              </button>
              <div className="border-t border-gray-100 my-1" />
              <p className="px-3 py-1 text-xs text-gray-500 font-medium uppercase">Raport Shpenzimeve</p>
              <button
                onClick={() => handleExportReport('expense', 'pdf')}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <FileText className="w-4 h-4 text-red-500" />
                PDF
              </button>
              <button
                onClick={() => handleExportReport('expense', 'excel')}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4 text-green-500" />
                Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content based on tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Grafiku i të Ardhurave & Fitimit
            </h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Të Ardhura</span>
                  <span className="text-sm font-bold text-green-600">{formatPrice(dailyData?.totals?.revenue_lek)} L</span>
                </div>
                <SimpleBarChart 
                  data={dailyData?.revenue} 
                  labels={dailyData?.labels} 
                  color="bg-green-500" 
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Shpenzime</span>
                  <span className="text-sm font-bold text-red-600">{formatPrice(dailyData?.totals?.expenses_lek)} L</span>
                </div>
                <SimpleBarChart 
                  data={dailyData?.expenses} 
                  labels={dailyData?.labels} 
                  color="bg-red-500" 
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Fitimi</span>
                  <span className={`text-sm font-bold ${(dailyData?.totals?.profit_lek || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                    {formatPrice(dailyData?.totals?.profit_lek)} L
                  </span>
                </div>
                <SimpleBarChart 
                  data={dailyData?.profit} 
                  labels={dailyData?.labels} 
                  color={(dailyData?.totals?.profit_lek || 0) >= 0 ? 'bg-emerald-500' : 'bg-orange-500'} 
                />
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div className="space-y-6">
            {/* Top Drivers */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Shoferët më të Mirë
              </h3>
              <div className="space-y-3">
                {topPerformers?.top_drivers?.length === 0 ? (
                  <p className="text-gray-500 text-sm">Nuk ka të dhëna</p>
                ) : (
                  topPerformers?.top_drivers?.slice(0, 5).map((driver, idx) => (
                    <div key={driver.driver_id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                        idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{driver.driver_name}</p>
                        <p className="text-xs text-gray-500">{driver.rides} udhëtime</p>
                      </div>
                      <span className="text-sm font-bold text-green-600">{formatPrice(driver.revenue_lek)} L</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Expenses by Category */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-red-600" />
                Shpenzime sipas Kategorisë
              </h3>
              <div className="space-y-3">
                {!summary?.expenses_by_category || Object.keys(summary.expenses_by_category).length === 0 ? (
                  <p className="text-gray-500 text-sm">Nuk ka shpenzime për këtë periudhë</p>
                ) : (
                  Object.entries(summary.expenses_by_category).map(([cat, amount]) => {
                    const Icon = getCategoryIcon(cat);
                    const percentage = summary.total_expenses_lek > 0 ? (amount / summary.total_expenses_lek) * 100 : 0;
                    return (
                      <div key={cat} className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getCategoryColor(cat)}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">{getCategoryLabel(cat)}</span>
                            <span className="text-sm font-bold text-gray-900">{formatPrice(amount)} L</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div 
                              className="bg-red-500 h-2 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 w-12 text-right">{percentage.toFixed(0)}%</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'by-entity' && (
        <div className="space-y-6">
          {/* Entity Type Selector */}
          <div className="flex items-center gap-2">
            {[
              { key: 'driver', label: 'Sipas Shoferit', icon: Users },
              { key: 'vehicle', label: 'Sipas Automjetit', icon: Car },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setEntityType(opt.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                  entityType === opt.key 
                    ? 'bg-red-600 text-white' 
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <opt.icon className="w-4 h-4" />
                {opt.label}
              </button>
            ))}
          </div>

          {/* Entity Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    {entityType === 'driver' ? 'Shoferi' : 'Automjeti'}
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Të Ardhura</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Cash</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Kartë</th>
                  {entityType === 'vehicle' && (
                    <>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Shpenzime</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Fitimi</th>
                    </>
                  )}
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Udhëtime</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">KM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={entityType === 'vehicle' ? 8 : 6} className="px-4 py-12 text-center">
                      <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">Duke ngarkuar...</p>
                    </td>
                  </tr>
                ) : !revenueByEntity?.data?.length ? (
                  <tr>
                    <td colSpan={entityType === 'vehicle' ? 8 : 6} className="px-4 py-12 text-center">
                      <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">Nuk ka të dhëna për këtë periudhë</p>
                    </td>
                  </tr>
                ) : (
                  revenueByEntity.data.map((item) => (
                    <tr key={item.entity_id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            entityType === 'driver' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {entityType === 'driver' ? (
                              <Users className="w-5 h-5" />
                            ) : (
                              <Car className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{item.entity_name}</p>
                            {entityType === 'vehicle' && item.plate && (
                              <p className="text-xs text-gray-500">{item.plate}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-semibold text-green-600">{formatPrice(item.total_revenue_lek)} L</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-700">{formatPrice(item.revenue_cash_lek)} L</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-700">{formatPrice(item.revenue_card_lek)} L</span>
                      </td>
                      {entityType === 'vehicle' && (
                        <>
                          <td className="px-4 py-3 text-right">
                            <span className="text-red-600">{formatPrice(item.total_expenses_lek)} L</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-semibold ${(item.profit_lek || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                              {formatPrice(item.profit_lek)} L
                            </span>
                          </td>
                        </>
                      )}
                      <td className="px-4 py-3 text-center">
                        <span className="text-gray-700">{item.rides_count}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-500">{formatPrice(item.distance_km)} km</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {revenueByEntity?.totals && (
                <tfoot className="bg-gray-100 font-medium">
                  <tr>
                    <td className="px-4 py-3 text-gray-900">TOTAL</td>
                    <td className="px-4 py-3 text-right text-green-600">{formatPrice(revenueByEntity.totals.total_revenue_lek)} L</td>
                    <td className="px-4 py-3 text-right">-</td>
                    <td className="px-4 py-3 text-right">-</td>
                    {entityType === 'vehicle' && (
                      <>
                        <td className="px-4 py-3 text-right text-red-600">{formatPrice(revenueByEntity.totals.total_expenses_lek)} L</td>
                        <td className="px-4 py-3 text-right text-emerald-600">{formatPrice(revenueByEntity.totals.total_profit_lek)} L</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-center">{revenueByEntity.totals.total_rides}</td>
                    <td className="px-4 py-3 text-right">-</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {activeTab === 'expenses' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Automjeti</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Kategoria</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Përshkrimi</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Pagesa</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Shuma</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">Duke ngarkuar...</p>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Nuk ka shpenzime për këtë periudhë</p>
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => {
                  const Icon = getCategoryIcon(expense.category);
                  return (
                    <tr key={expense.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">{formatDate(expense.expense_date)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-700">
                            <Car className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{expense.vehicle_plate || '-'}</p>
                            <p className="text-xs text-gray-500">{expense.vehicle_model || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(expense.category)}`}>
                          <Icon className="w-3 h-3" />
                          {getCategoryLabel(expense.category)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{expense.description}</span>
                        {expense.liters && (
                          <span className="text-xs text-gray-400 ml-2">({expense.liters}L)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          expense.payment_method === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {expense.payment_method === 'cash' ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                          {expense.payment_method === 'cash' ? 'Cash' : 'Kartë'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-semibold text-red-600">{formatPrice(expense.amount_lek)} L</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals Bar */}
      <div className="bg-gray-900 rounded-xl p-4 flex items-center justify-between text-white">
        <div className="flex items-center gap-8">
          <div>
            <p className="text-gray-400 text-xs">Periudha</p>
            <p className="text-lg font-bold">{getDateFilterLabel()}</p>
          </div>
          <div className="w-px h-10 bg-gray-700" />
          <div>
            <p className="text-gray-400 text-xs">Të Ardhura</p>
            <p className="text-xl font-bold text-green-400">{formatPrice(summary?.total_revenue_lek)} L</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs">Shpenzime</p>
            <p className="text-xl font-bold text-red-400">{formatPrice(summary?.total_expenses_lek)} L</p>
          </div>
          {vatSettings.vat_enabled && (
            <div>
              <p className="text-gray-400 text-xs">TVSH</p>
              <p className="text-xl font-bold text-purple-400">{formatPrice(summary?.total_vat_collected_lek)} L</p>
            </div>
          )}
        </div>
        <div className="text-right">
          <p className="text-gray-400 text-xs">FITIMI NETO</p>
          <p className={`text-3xl font-bold ${(summary?.net_profit_lek || 0) >= 0 ? 'text-yellow-400' : 'text-orange-400'}`}>
            {formatPrice(summary?.net_profit_lek)} L
          </p>
        </div>
      </div>

      {/* VAT Settings Modal */}
      {showVatSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Konfigurimet e TVSH</h2>
              <button onClick={() => setShowVatSettingsModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* VAT Enabled Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Aktivizo TVSH</p>
                  <p className="text-sm text-gray-500">Apliko TVSH në faturat e rrugëve</p>
                </div>
                <button
                  onClick={() => setVatSettings({ ...vatSettings, vat_enabled: !vatSettings.vat_enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    vatSettings.vat_enabled ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      vatSettings.vat_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {vatSettings.vat_enabled && (
                <>
                  {/* VAT Percentage */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Përqindja e TVSH (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={vatSettings.vat_percentage}
                      onChange={(e) => setVatSettings({ ...vatSettings, vat_percentage: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  {/* VAT Registration Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nr. NIPT (opsionale)</label>
                    <input
                      type="text"
                      value={vatSettings.vat_registration_number || ''}
                      onChange={(e) => setVatSettings({ ...vatSettings, vat_registration_number: e.target.value })}
                      placeholder="L12345678K"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  {/* VAT Included in Price */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">TVSH i përfshirë në çmim</p>
                      <p className="text-sm text-gray-500">Çmimi i shfaqur përfshin TVSH</p>
                    </div>
                    <button
                      onClick={() => setVatSettings({ ...vatSettings, vat_included_in_price: !vatSettings.vat_included_in_price })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                        vatSettings.vat_included_in_price ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          vatSettings.vat_included_in_price ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-gray-200 p-4 flex gap-3">
              <button
                onClick={() => setShowVatSettingsModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Anulo
              </button>
              <button
                onClick={handleUpdateVatSettings}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
              >
                Ruaj Ndryshimet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Shto Shpenzim të Ri</h2>
              <button onClick={() => setShowAddExpenseModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Vehicle Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Automjeti *</label>
                <select
                  value={newExpense.vehicle_id}
                  onChange={(e) => setNewExpense({ ...newExpense, vehicle_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Zgjidh Automjetin</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.plate} - {v.brand} {v.model}</option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria *</label>
                <select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="fuel">Karburant</option>
                  <option value="service">Servis</option>
                  <option value="insurance">Sigurim</option>
                  <option value="tax">Taksë</option>
                  <option value="inspection">Kolaudim</option>
                  <option value="repair">Riparim</option>
                  <option value="salary">Rroga</option>
                  <option value="other">Tjetër</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Përshkrimi *</label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  placeholder="psh. Karburant Kastrati"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shuma (LEK) *</label>
                <input
                  type="number"
                  value={newExpense.amount_lek}
                  onChange={(e) => setNewExpense({ ...newExpense, amount_lek: e.target.value })}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Payment Method & Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mënyra e Pagesës</label>
                  <select
                    value={newExpense.payment_method}
                    onChange={(e) => setNewExpense({ ...newExpense, payment_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Kartë</option>
                    <option value="transfer">Transfer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                  <input
                    type="date"
                    value={newExpense.expense_date}
                    onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              {/* Fuel specific fields */}
              {newExpense.category === 'fuel' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kilometra (odometër)</label>
                    <input
                      type="number"
                      value={newExpense.odometer_km}
                      onChange={(e) => setNewExpense({ ...newExpense, odometer_km: e.target.value })}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Litra</label>
                    <input
                      type="number"
                      value={newExpense.liters}
                      onChange={(e) => setNewExpense({ ...newExpense, liters: e.target.value })}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shënime</label>
                <textarea
                  value={newExpense.notes}
                  onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                  placeholder="Shënime shtesë..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 flex gap-3">
              <button
                onClick={() => setShowAddExpenseModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Anulo
              </button>
              <button
                onClick={handleAddExpense}
                disabled={!newExpense.vehicle_id || !newExpense.description || !newExpense.amount_lek}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Shto Shpenzimin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancePage;
